import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
  FaTruck, FaTrailer, FaUser, FaPlus, FaTrash, FaTimes,
  FaSpinner, FaBuilding, FaCogs, FaClipboardList
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';

// Shared fetch helper (same pattern as SubbieFleet / LoadPermitsPanel)
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
};

// Admin Fleet Overview - see ALL subbies' fleet, add/edit/delete on behalf.
function AdminFleetOverview({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subbies, setSubbies] = useState([]);
  const [selectedSubbieId, setSelectedSubbieId] = useState('');
  const [fleet, setFleet] = useState({ trucks: [], trailers: [], cranes: [], drivers: [] });
  const [activeTab, setActiveTab] = useState('trucks');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('truck');
  const [formData, setFormData] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchSubbies(); }, []);

  const fetchSubbies = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/subcontractors');
      const list = data.subcontractors || [];
      setSubbies(list);
      if (list.length > 0) {
        setSelectedSubbieId(list[0].id);
        fetchFleet(list[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading subbies:', err);
      toast.error('Failed to load subcontractors');
      setLoading(false);
    }
  };

  const fetchFleet = async (subbieId) => {
    setLoading(true);
    try {
      const [t, tr, c, d] = await Promise.all([
        apiFetch(`/trucks?subcontractor_id=${subbieId}`),
        apiFetch(`/trailers?subcontractor_id=${subbieId}`),
        apiFetch(`/cranes?subcontractor_id=${subbieId}`),
        apiFetch(`/drivers?subcontractor_id=${subbieId}`)
      ]);
      setFleet({
        trucks: t.trucks || [],
        trailers: tr.trailers || [],
        cranes: c.cranes || [],
        drivers: d.drivers || []
      });
    } catch (err) {
      console.error('Error loading fleet:', err);
      toast.error('Failed to load fleet');
    } finally {
      setLoading(false);
    }
  };

  const endpointFor = (type) => {
    if (type === 'truck') return '/trucks';
    if (type === 'trailer') return '/trailers';
    if (type === 'crane') return '/cranes';
    return '/drivers';
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      // parseFloat('') is NaN, and JSON.stringify turns NaN into null — which Postgres
      // rejects on a NOT NULL column. This is exactly what broke "Add Trailer"
      // (capacity_mt is NOT NULL but the form has no capacity box). Strip NaN to null.
      const payload = { ...formData, subcontractor_id: selectedSubbieId };
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === 'number' && !Number.isFinite(payload[k])) payload[k] = null;
      });

      const data = await apiFetch(endpointFor(addType), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.success) {
        if (addType === 'driver' && data.temporary_password) {
          toast.success(`Driver added. Temp password: ${data.temporary_password}`, { autoClose: 15000 });
        } else {
          toast.success('Added successfully');
        }
        setShowAddModal(false);
        setFormData({});
        fetchFleet(selectedSubbieId);
      } else {
        toast.error(data.error || 'Failed to add');
      }
    } catch (err) {
      console.error('Error adding:', err);
      toast.error('Failed to add');
    } finally {
      setProcessing(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // { type, id }

  const handleDelete = (type, id) => {
    setDeleteTarget({ type, id });
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`${endpointFor(deleteTarget.type)}/${deleteTarget.id}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchFleet(selectedSubbieId);
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  const openAdd = (type) => {
    setAddType(type);
    setFormData({});
    setShowAddModal(true);
  };

  const tabs = [
    { key: 'trucks', label: 'Trucks', icon: <FaTruck size={14} />, count: fleet.trucks.length },
    { key: 'trailers', label: 'Trailers', icon: <FaTrailer size={14} />, count: fleet.trailers.length },
    { key: 'cranes', label: 'Cranes', icon: <FaCogs size={14} />, count: fleet.cranes.length },
    { key: 'drivers', label: 'Drivers', icon: <FaUser size={14} />, count: fleet.drivers.length }
  ];

  const currentList = fleet[activeTab] || [];
  const itemType = activeTab === 'trucks' ? 'truck' : activeTab === 'trailers' ? 'trailer' : activeTab === 'cranes' ? 'crane' : 'driver';

  return (
    <AdminLayout activeTab="fleet-overview" onLogout={onLogout}>
      <div className="mb-6 flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">Fleet Overview</h1>
          <p className="text-sm text-gray-500">Every subbie's trucks, trailers, cranes and drivers - in one place.</p>
        </div>
        <button
          onClick={() => navigate(selectedSubbieId ? `/admin-scheduler?subbie=${selectedSubbieId}` : '/admin-scheduler')}
          className="px-4 py-2 bg-navy text-white rounded-lg text-sm flex items-center gap-2"
        >
          <FaClipboardList size={14} /> Schedule this subbie's fleet
        </button>
      </div>

      <div className="bg-white rounded-xl p-4 mb-4 border border-gray-200">
        <label className="block text-xs font-medium text-gray-500 mb-1">Select subcontractor</label>
        <select
          value={selectedSubbieId}
          onChange={(e) => { setSelectedSubbieId(e.target.value); fetchFleet(e.target.value); }}
          className="w-full max-w-md px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
        >
          {subbies.map(s => (
            <option key={s.id} value={s.id}>{s.company_name || 'Unnamed'}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 border ${activeTab === t.key ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:border-navy'}`}
          >
            {t.icon} {t.label} <span className="text-xs opacity-70">{t.count}</span>
          </button>
        ))}
        <button
          onClick={() => openAdd(itemType)}
          className="ml-auto px-4 py-2 bg-green text-white rounded-lg text-sm flex items-center gap-2"
        >
          <FaPlus size={14} /> Add {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <FaSpinner className="animate-spin text-navy" size={24} />
        </div>
      ) : currentList.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <FaBuilding className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-400">No {activeTab} for this subcontractor yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentList.map(item => (
            <div key={item.id} className="bg-white rounded-xl p-4 border border-gray-200 flex justify-between items-start">
              <div>
                {activeTab === 'trucks' && (
                  <>
                    <h4 className="font-bold text-navy">{item.unit_name || item.registration_number || 'Truck'}</h4>
                    <p className="text-sm text-gray-600">{item.make || ''} {item.model || ''}</p>
                    <p className="text-xs text-gray-500">{item.truck_type ? item.truck_type.replace(/_/g, ' ') : ''}{item.registration_number ? ` · ${item.registration_number}` : ''}</p>
                  </>
                )}
                {activeTab === 'trailers' && (
                  <>
                    <h4 className="font-bold text-navy">{item.trailer_name || item.registration_number || 'Trailer'}</h4>
                    <p className="text-sm text-gray-600">{item.trailer_type ? item.trailer_type.replace(/_/g, ' ') : ''}</p>
                    <p className="text-xs text-gray-500">{item.axle_config ? `${item.axle_config} · ` : ''}{item.capacity_mt ? `${item.capacity_mt} MT` : ''}</p>
                  </>
                )}
                {activeTab === 'cranes' && (
                  <>
                    <h4 className="font-bold text-navy">{item.unit_name || `${item.make || ''} ${item.model || ''}`.trim() || 'Crane'}</h4>
                    <p className="text-sm text-gray-600">{item.crane_type ? item.crane_type.replace(/_/g, ' ') : 'Crane'}</p>
                    <p className="text-xs text-gray-500">{item.max_capacity_tonnes ? `${item.max_capacity_tonnes} t` : ''}{item.boom_length_m ? ` · ${item.boom_length_m} m boom` : ''}</p>
                  </>
                )}
                {activeTab === 'drivers' && (
                  <>
                    <h4 className="font-bold text-navy">{item.full_name || 'Driver'}</h4>
                    <p className="text-sm text-gray-600">ID: {item.id_number || 'N/A'}</p>
                    <p className="text-xs text-gray-500">PrDP: {item.prdp_number || 'N/A'}{item.phone ? ` · ${item.phone}` : ''}</p>
                  </>
                )}
              </div>
              <button onClick={() => handleDelete(itemType, item.id)} className="text-gray-400 hover:text-red-500">
                <FaTrash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Add {addType.charAt(0).toUpperCase() + addType.slice(1)}</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            <form onSubmit={handleAdd} className="p-6">
              <div className="space-y-4">
                {addType === 'truck' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Truck Type</label>
                      <select value={formData.truck_type || ''} onChange={(e) => setFormData({ ...formData, truck_type: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
                        <option value="">Select...</option>
                        <option value="prime_mover">Prime Mover</option>
                        <option value="rigid">Rigid</option>
                        <option value="side_lifter">Side Lifter</option>
                        <option value="crane_truck">Crane Truck (Hiab)</option>
                        <option value="tipper">Tipper</option>
                        <option value="tanker">Tanker</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <Input label="Unit Name" value={formData.unit_name} onChange={(v) => setFormData({ ...formData, unit_name: v })} />
                    <Input label="Registration Number *" value={formData.registration_number} onChange={(v) => setFormData({ ...formData, registration_number: v })} required />
                    <Input label="Make" value={formData.make} onChange={(v) => setFormData({ ...formData, make: v })} />
                    <Input label="Model" value={formData.model} onChange={(v) => setFormData({ ...formData, model: v })} />
                  </>
                )}
                {addType === 'trailer' && (
                  <>
                    <Input label="Registration Number *" value={formData.registration_number} onChange={(v) => setFormData({ ...formData, registration_number: v })} required />
                    <Input label="Trailer Name" value={formData.trailer_name} onChange={(v) => setFormData({ ...formData, trailer_name: v })} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trailer Type</label>
                      <select value={formData.trailer_type || ''} onChange={(e) => setFormData({ ...formData, trailer_type: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
                        <option value="">Select...</option>
                        <option value="side_tipper">Side Tipper</option>
                        <option value="grain_trailer">Grain Trailer</option>
                        <option value="flatbed">Flatbed</option>
                        <option value="flatdeck">Flatdeck</option>
                        <option value="tautliner">Tautliner</option>
                        <option value="lowbed">Lowbed</option>
                        <option value="superlink">Superlink</option>
                        <option value="tanker">Tanker</option>
                      </select>
                    </div>
                    <Input label="Axle Config" value={formData.axle_config} onChange={(v) => setFormData({ ...formData, axle_config: v })} />
                  </>
                )}
                {addType === 'crane' && (
                  <>
                    <Input label="Unit Name" value={formData.unit_name} onChange={(v) => setFormData({ ...formData, unit_name: v })} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Crane Type</label>
                      <select value={formData.crane_type || ''} onChange={(e) => setFormData({ ...formData, crane_type: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
                        <option value="">Select...</option>
                        <option value="mobile">Mobile</option>
                        <option value="tower">Tower</option>
                        <option value="crawler">Crawler</option>
                        <option value="truck_mounted">Truck-Mounted</option>
                        <option value="all_terrain">All-Terrain</option>
                      </select>
                    </div>
                    <Input label="Make" value={formData.make} onChange={(v) => setFormData({ ...formData, make: v })} />
                    <Input label="Model" value={formData.model} onChange={(v) => setFormData({ ...formData, model: v })} />
                    <Input label="Max Capacity (t)" value={formData.max_capacity_tonnes} onChange={(v) => setFormData({ ...formData, max_capacity_tonnes: v })} />
                    <Input label="Boom Length (m)" value={formData.boom_length_m} onChange={(v) => setFormData({ ...formData, boom_length_m: v })} />
                  </>
                )}
                {addType === 'driver' && (
                  <>
                    <Input label="Full Name *" value={formData.full_name} onChange={(v) => setFormData({ ...formData, full_name: v })} required />
                    <Input label="Phone" value={formData.phone} onChange={(v) => setFormData({ ...formData, phone: v })} />
                    <Input label="ID Number" value={formData.id_number} onChange={(v) => setFormData({ ...formData, id_number: v })} />
                    <Input label="PrDP Number" value={formData.prdp_number} onChange={(v) => setFormData({ ...formData, prdp_number: v })} />
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">Cancel</button>
                <button type="submit" disabled={processing} className="px-4 py-2 bg-green text-white rounded-lg text-sm disabled:opacity-50">
                  {processing ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete"
        message={deleteTarget ? `Delete this ${deleteTarget.type}?` : 'Delete this item?'}
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
      />
    </AdminLayout>
  );
}

function Input({ label, value, onChange, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
        required={required}
      />
    </div>
  );
}

export default AdminFleetOverview;